import * as StringUtils from '../../utils/string-utils';
import { productTypeServiceModule } from '../product-type-service/product-type-service';
import { configServiceModule } from '../config-service/config-service';
import { compareIgnoreCase } from '../../utils/string-utils';
import { defineValue } from '../../utils/object-utils/object-utils';

export const billingFeaturesServiceModule = angular
  .module('Q.services.billing-features-service', [
    productTypeServiceModule,
    configServiceModule,
  ])
  .factory('billingFeaturesService', function (PT, configService) {
    let config = {};

    const load = () => {
      return configService
        .load()
        .then((cfg) => Object.assign(config, cfg.billing));
    };

    // returns the default for the plan, useful for when the feature does not have a specified value
    const featureDefault = (name, featureSet = 'trial') => {
      const feature = config.features[name];
      if (!feature) return;

      if (feature.enum) {
        // for enums, we first try to match a value to the feature set
        let value = null;
        Object.keys(feature.enum).forEach((key) => {
          const val = feature.enum[key];
          if (val.featureSets && val.featureSets.indexOf(featureSet) >= 0) {
            value = key;
          }
        });
        // return that value, or the default if no feature set specific value was found
        return value || feature.default;
      } else if (feature.featureSets) {
        return feature.featureSets.indexOf(featureSet) >= 0;
      } else {
        return feature.alwaysOn;
      }
    };

    const catalog = (features) => {
      // we start off with the extra features which are not actually tracked as features (because they are standard),
      // but that we wish to still display within the list.
      let featureSet = {
        assess: {
          priority: -2,
          image: '/shared/images/qualified-assess-horizontal.svg',
          alt: 'Qualified Assess',
          enabled: true,
          tooltip: `Send Challenges to quickly and painlessly measure your ${PT.participants}' ability`,
        },
        interact: {
          priority: -1,
          image: '/shared/images/qualified-interact-horizontal.svg',
          alt: 'Qualified Interact',
          enabled: true,
          tooltip: `Live sessions between a ${PT.participant} and one or more team member(s)`,
        },
      };

      defineValue(featureSet, 'asList', Object.values(featureSet));

      // the server stores participant slug values which need to be dynamically replaced
      const participate = (text, up) => {
        if (text) {
          return text
            .replace('@participant', up ? PT.Participant : PT.participant)
            .replace('@participants', up ? PT.Participants : PT.participants);
        }
      };

      load().then(() => {
        // weave in the features returned from the server
        Object.keys(config.features || {}).forEach((key) => {
          const cfg = (featureSet[key] = angular.copy(config.features[key]));
          cfg.displayName = participate(cfg.displayName, true);
          cfg.tooltip = participate(cfg.blurb);

          // track enum values so that we can display those properly
          if (cfg.enum) {
            cfg.value =
              features[key] || featureDefault(key, features.baseFeatureSet);
            cfg.label = StringUtils.upperCaseWords(cfg.value);
            cfg.enabled = !!cfg.value;
            cfg.values = Object.keys(cfg.enum);
            cfg.upgradeValue = cfg.values[cfg.values.length - 1];
            cfg.upgradeLabel = StringUtils.upperCaseWords(cfg.upgradeValue);
            cfg.upgradable = cfg.label !== cfg.upgradeLabel;
          } else {
            cfg.enabled =
              features &&
              (features[key] === true ||
                (features[key] !== false &&
                  featureDefault(key, features.baseFeatureSet)));
          }

          cfg.defaultUpgradeDisplay = cfg.defaultDisplay =
            cfg.shortDisplay || cfg.displayName;
          if (cfg.label) {
            cfg.defaultDisplay += `: ${cfg.label}`;
          }
          if (cfg.upgradeLabel) {
            cfg.defaultUpgradeDisplay += `: ${cfg.upgradeLabel}`;
          }

          // setup a priority, which is used for sorting alphabetically but first by
          // priority so that feature set features are not spread out all over the place.
          // since by default, we choose the first plan to list features, and that plan should
          // be the lowest, this should result in a decent order to display
          cfg.priority = cfg.enabled || cfg.enum ? 2 : 3;
        });

        defineValue(
          featureSet,
          'asList',
          Object.values(featureSet).sort((a, b) => {
            return (
              a.priority - b.priority ||
              compareIgnoreCase(a.defaultDisplay, b.defaultDisplay)
            );
          }),
        );
      });

      return featureSet;
    };

    return {
      featureDefault,
      config,
      load,
      catalog,
    };
  });
